/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect, useMemo } from "react";
import {
  Avatar,
  Box,
  Stack,
  Tab,
  Tabs,
  ThemeProvider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuList,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import _ from "lodash";
import { useImmer } from "use-immer";
import { useCookies } from "react-cookie";
import { useParams } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useAuth from "../../hooks/use-auth";
import CustomToolBar from "../../components/custom-toolbar";
import CustomFooter from "../../components/custom-footer";
import ChangePd from "../../components/change-pd";
import Gallery from "../../components/gallery";
import CompanyCard from "../../components/company-card";
import theme from "../../theme";
import moment from "moment";
import numeral from "numeral";
import { GetPayRecords } from "../../utils/gql-api-service";
import { useLazyQuery } from "@apollo/client";
import useApi from "../../hooks/use-api";
import useCompany from "../../hooks/use-company";

const companyDefId = { 
  bannan: "21", 
  "temp-staging-bannan": "21"
};


const Home = () => {
  // #region CSS
  const tabBtn = css({
    border: "1px solid #009043",
    borderRadius: "10px",
    paddingInline: "45px",
    marginRight: "10px",
    color: "#009043",
  });
  const qusTitle = css({
    fontWeight: 500,
    fontSize: "14px",
    color: "#2B2B2B",
    lineHeight: "25px",
  });
  const ansText = css({
    fontWeight: 400,
    fontSize: "12px",
    color: "#2B2B2B",
    overflowWrap: "break-word",
  });
  const menuCell = css({
    borderBottom: "1px solid #E6E6E6",
    wordWrap: "break-word",
    wordBreak: "break-all",
    whiteSpace: "normal",
  });
  const menuCellSelected = css({
    borderBottom: "1px solid #009043",
    color: "#009043",
    wordWrap: "break-word",
    wordBreak: "break-all",
    whiteSpace: "normal",
    "& hr": {
      borderColor: "#009043",
      margin: "5px 8px 5px 0px",
    },
  });
  const tableHolder = css({
    height: 400,
    width: "100%",
    overflowX: "auto",
    ".MuiDataGrid-root":{
      minWidth:500
    },
    ".data-grid-header": {
      backgroundColor: "#2b2b2b",
      color: "white",
      fontSize: 12,
    },
    ".MuiDataGrid-selectedRowCount": {
      visibility: "hidden",
    },
    ".MuiDataGrid-row": {
      cursor: "pointer",
    },
  });
  const qaHolder = css({
    height: 400,
    width: "100%",
    borderTop: "1px solid #0000001f",
    paddingInline: "5px",
    overflow: "scroll",
    ".image > img": {
      maxWidth: "100%",
      objectFit: "contain",
    },
  });
  const progressDesc = css({
    width: "100%",
    wordBreak: "break-word",
    color: "#2b2b2b",
  });
  const contentBox = css({
    display: "grid",
    width: "100%",
    gridTemplateColumns: "1fr auto 1fr",
    gridTemplateRows: "100%",
    columnGap: "10px",
    "@media(max-width: 600px)": {
      width: "auto",
      display: "block",
      padding: "10px"
    },
  });
  // #endregion

  // #region Data
  const [getPayRecord, { data: payData, loading }] = useLazyQuery(
    GetPayRecords,
    {
      fetchPolicy: "network-only",
    }
  );
  const { token, signOut } = useAuth();
  const {
    setEDepartmentId,
    companyInfo: companyData,
    buildingCaseInfo,
  } = useCompany();
  const [cookie] = useCookies(["creCareToken"]);
  const {
    getProfile,
    getProgressList,
    getProgressDetail,
    getProblemList,
    patchCRECareChangePd,
    getContactInfo,
  } = useApi();
  const { eDepartmentId } = useParams();
  const tabData = ["工程進度", "繳款紀錄"];
  const tabIndex = {
    progress: 0,
    payRecord: 1,
    cre: 2,
    problem: 3,
    changePd: 4,
  };
  const columns = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "進度主題",
        headerClassName: "data-grid-header",
        width: 250,
        sortable: false,
      },
      {
        field: "description",
        headerName: "摘要",
        headerClassName: "data-grid-header",
        flex: 1,
        sortable: false,
      },
      {
        field: "publishDate",
        headerName: "發布日期",
        headerClassName: "data-grid-header",
        width: 180,
        sortable: false,
        renderCell: ({ row }) => moment(row.publishDate).format("YYYY-MM-DD"),
      },
    ];
  }, []);
  const payCols = useMemo(() => {
    return [
      {
        field: "term",
        headerName: "期別",
        headerClassName: "data-grid-header",
        flex: 1,
        sortable: false,
      },
      {
        field: "shouldPayDate",
        headerName: "應繳日期",
        headerClassName: "data-grid-header",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          moment(row.shouldPayDate).isValid()
            ? moment(row.shouldPayDate).format("YYYY-MM-DD")
            : "",
      },
      {
        field: "shouldPayAmount",
        headerName: "合約金額",
        headerClassName: "data-grid-header",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => numeral(row.shouldPayAmount).format("0,0"),
      },
      {
        field: "realPayDate",
        headerName: "實繳日期",
        headerClassName: "data-grid-header",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          moment(row.realPayDate).isValid()
            ? moment(row.realPayDate).format("YYYY-MM-DD")
            : "",
      },
      {
        field: "realPayAmount",
        headerName: "已繳金額",
        headerClassName: "data-grid-header",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => numeral(row.realPayAmount).format("0,0"),
      },
    ];
  }, []);

  const [state, produce] = useImmer({
    tabValue: 1,
    menuValue: 0,
    isShow: false,
    isLoading: false,
    isNeedSignOut: false,
    dilogMsg: null,
    progressRows: [],
    payRows: [],
    qaRows: [],
    menuData: [],
    imageData: [],
    eBuildingCaseId: "",
    don: "",
    household: "",
    progressDetail: null,
    companyInfo: {
      name: "",
      phone: "",
      taxId: "",
      address: "",
    },
    userName: "",
    userNameId: "",
    paths: [
      { title: "首頁", link: `/${eDepartmentId}/home` },
      { title: "會員登出", link: `/${eDepartmentId}/login`, isSignOut: true },
    ],
  });
  const {
    tabValue,
    menuValue,
    isShow,
    isLoading,
    isNeedSignOut,
    dilogMsg,
    progressRows,
    payRows,
    qaRows,
    menuData,
    imageData,
    eBuildingCaseId,
    don,
    household,
    progressDetail,
    companyInfo,
    userName,
    userNameId,
    paths,
  } = state;
  // #endregion

  // Component
  const ProblemAccordion = ({ qus, ans }) => {
    return (
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#009043" }} />}
        >
          <Stack direction="row" spacing={1} alignContent="center">
            <Avatar
              sx={{
                width: "25px",
                height: "25px",
                backgroundColor: "#009043",
                fontSize: "14px",
              }}
            >
              Q
            </Avatar>
            <Box css={qusTitle}>{qus}</Box>{" "}
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          css={ansText}
          dangerouslySetInnerHTML={{ __html: ans }}
        ></AccordionDetails>
      </Accordion>
    );
  };

  // #region Method
  const onTabChange = (event, value) => {
    if (value === tabIndex.cre) return;
    produce((draft) => void (draft.tabValue = value));
    if (value === tabIndex.progress) {
      getProgressListData();
    } else if (value === tabIndex.problem) {
      getProblemListData();
    }
  };
  const onSendChangePd = (params) => {
    const creCareToken = cookie.cres_token;
    const headers = { Authorization: `Bearer ${creCareToken}` };
    patchCRECareChangePd({
      params,
      headers,
      onSuccess: (result) => {
        produce((draft) => void (draft.dilogMsg = result.message));
      },
      onFail: (result) => {
        console.log("[Test] onSendChangePd  onFail: ", result);
      },
    });
  };
  const onClickMenu = (value) => {
    produce((draft) => {
      draft.menuValue = value;
      draft.eBuildingCaseId = menuData[value].eBuildingCaseId;
      draft.don = menuData[value].don;
      draft.household = menuData[value].household;
    });
  };
  const onClickCell = (params, event) => {
    // event.stopPropagation();
    const { row: rowData } = params;
    getProgressDetailData(rowData.projectProgressId);
    produce((draft) => void (draft.isShow = true));
  };
  const onClickCreTabBtn = () => {
    // console.log("[Test] cookie.cres_token :", cookie.cres_token);
    window.open(`${process.env.REACT_APP_SERVER_URL}?name=${eDepartmentId}&jt=${cookie.cres_token}`, "_blank");
    // window.open(`https://customize.cathay-red.com.tw`, "_blank");
    // window.open(
    //   `${process.env.REACT_APP_SERVER_URL}/home?jt=${cookie.cres_token}`,
    //   "_blank"
    // );
  };
  const onCloseDialog = () => {
    produce((draft) => void (draft.isShow = false));
  };
  const onCloseMsgDialog = () => {
    produce((draft) => void (draft.dilogMsg = null));
    isNeedSignOut && signOut();
  };
  const onMenuChange = () => {
    switch (tabValue) {
      case tabIndex.progress:
        produce((draft) => void (draft.progressRows = []));
        getProgressListData();
        break;
      case tabIndex.payRecord:
        getPayRecord({
          variables: {
            eBuildingCaseId,
            identityNumber: userNameId,
            don,
            household,
          },
        });
        break;
      case tabIndex.problem:
        produce((draft) => void (draft.qaRows = []));
        getProblemListData();
        break;
      default:
        break;
    }
  };

  const getProfileData = () => {
    if (!token) {
      signOut();
      return;
    }
    // produce((draft) => void (draft.isLoading = true));
    const headers = { Authorization: `Bearer ${token}` };
    getProfile({
      headers,
      depId: companyDefId[eDepartmentId] /* eDepartmentId */,
      onSuccess: (result) => {
        handleMenuData(result);
      },
      onFail: (result) => {
        console.log("[Test] getProfileData  onFail: ", result);
      },
    });
  };
  const getProgressListData = () => {
    produce((draft) => void (draft.isLoading = true));
    getProgressList({
      eBuildingCaseId,
      onSuccess: (result) => {
        produce((draft) => {
          draft.progressRows = result.projectProgressVoList;
          draft.isLoading = false;
        });
      },
      onFail: (result) => {
        produce((draft) => {
          draft.isLoading = false
          draft.progressRows = [];
        });
        console.log("[Test] getProgressListData  onFail: ", result);
      },
    });
  };
  const getProgressDetailData = (projectProgressId) => {
    getProgressDetail({
      projectProgressId,
      onSuccess: (result) => {
        handleProgressData(result);
      },
      onFail: (result) => {
        console.log("[Test] getProgressDetailData  onFail: ", result);
      },
    });
  };
  const getProblemListData = () => {
    produce((draft) => void (draft.isLoading = true));
    getProblemList({
      eBuildingCaseId,
      onSuccess: (result) => {
        produce((draft) => {
          draft.qaRows = result.projectQuestionVoList.map(({question, answer}) => ({
            qus: question,
            ans: handleProblemData(answer),
          }));
          draft.isLoading = false;
        });
      },
      onFail: (result) => {
        produce((draft) => {
          draft.isLoading = false
          draft.qaRows = []
        });
        console.log("[Test] getProblemList onFail: ", result);
      },
    });
  };
  const getContactInfoData = (eBuildingCaseId) => {
    produce((draft) => void (draft.isLoading = true));
    getContactInfo({
      eBuildingCaseId,
      onSuccess: (result) => {     
        produce((draft) => {
          draft.companyInfo = {
            name: result.companyName,
            phone: result.companyPhone,
            taxId: result.companyTaxId,
            address: result.companyAddress,
            logoPath: process.env.REACT_APP_BASE_URL + `/${eBuildingCaseId}/contactInfo/logo`,
            bannerPath: process.env.REACT_APP_BASE_URL + `/${eBuildingCaseId}/contactInfo/banner`,
          }
          draft.isLoading = false;
        });
      },
      onFail: (result) => {
        console.log("[Test] getContactInfo onFail: ", result);
      },
    });
  };

  const handleMenuData = ({ buildingCases, nameId, payInfo }) => {
    if (!buildingCases || buildingCases.length === 0) {
      return;
    }
    if (!payInfo || payInfo.length === 0) {
      produce((draft) => {
        draft.dilogMsg = "查無紀錄，請洽服務人員";
        draft.isNeedSignOut = true;
      });
      return;
    }
    // const menu = _(buildingCases)
    //   .uniqBy("eBuildingCaseId")
    //   .map(({ eBuildingCaseId, buildingName }) => ({
    //     eBuildingCaseId,
    //     buildingName,
    //   }))
    //   .value();
    const menu = _.map(
      payInfo,
      ({
        eBuildingCaseId,
        eBuildingCaseName,
        donHouseholdDesc,
        don,
        household,
      }) => ({
        eBuildingCaseId,
        buildingName: eBuildingCaseName + " " + donHouseholdDesc,
        don,
        household,
      })
    );
    const tmpUserObj = _.find(buildingCases, (obj) => obj.name != null);
    getContactInfoData(tmpUserObj.eBuildingCaseId)

    produce((draft) => {
      draft.menuData = menu;
      menu.length > 0 && (draft.eBuildingCaseId = menu[0].eBuildingCaseId);
      menu.length > 0 && (draft.don = menu[0].don);
      menu.length > 0 && (draft.household = menu[0].household);
      draft.userName = !tmpUserObj ? "親愛的用戶" : tmpUserObj.name;
      draft.userNameId = nameId;
    });
  };
  const handleProgressData = (data) => {
    if (!data) {
      return;
    }
    const { publishDate, name, description } = data;
    const tmpProgressObj = {
      publishDate: moment(publishDate).format("YYYY/MM/DD"),
      name,
      description,
    };
    
    const tmpImgData = _.map(data.projectProgressImageIds, (obj) => ({
      title: obj.fileName,
      img: process.env.REACT_APP_BASE_URL + `/projectProgressImage/${obj}`,
    }));

    produce((draft) => {
      draft.progressDetail = tmpProgressObj;
      draft.imageData = tmpImgData;
    });
  };
  const handleCompanyData = (data) => {
    // if (!data) {
    //   return;
    // }
    // const { address, name, phone, taxId, logoPath, bannerPath } = data;
    // const tmpInfo = {
    //   name,
    //   phone,
    //   taxId,
    //   address,
    //   logoPath: process.env.REACT_APP_SERVER_URL + "/api/" + logoPath,
    //   bannerPath: process.env.REACT_APP_SERVER_URL + "/api/" + bannerPath,
    // };
    // produce((draft) => {
    //   draft.companyInfo = tmpInfo;
    // });
  };
  const handleProblemData = (htmlString) => {
    const oembedRegex = /<oembed[^>]*>/g;
    const oembedMatch = htmlString.match(oembedRegex);

    if (oembedMatch) {
      let origHtml = htmlString;
      _.forEach(oembedMatch, (matchObj) => {
        const oembedUrl = matchObj.match(/url="([^"]*)"/)[1];
        // 560 * 315
        const iframeElement = `<iframe width="100%" height="435" src="${oembedUrl}" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
        origHtml = origHtml.replace(matchObj, iframeElement);
      });
      return origHtml;
    }

    return htmlString;
  };
  // #endregion

  // #region UseEffect
  useEffect(() => {
    getProfileData();
  }, []);

  useEffect(() => {
    if (!payData) {
      return;
    }

    produce((draft) => {
      draft.payRows = _.map(payData.payRecords, (obj, index) => ({
        ...obj,
        id: index,
      }));
      draft.isLoading = false;
    });
  }, [payData]);

  useEffect(() => {
    if (eBuildingCaseId !== "") {
      // getCompanyData();
      onMenuChange();
    }
  }, [eBuildingCaseId, don, household]);

  useEffect(() => {
    !!eDepartmentId && setEDepartmentId(eDepartmentId);
  }, [eDepartmentId]);

  useEffect(() => {
    if (!!companyData) {
      produce(
        (draft) =>
          void (draft.paths = [
            { title: "首頁", link: `/${eDepartmentId}/home` },
            {
              title: companyData.name + "會員專區",
              link: `/${eDepartmentId}/home`,
            },
            {
              title: "會員登出",
              link: `/${eDepartmentId}/login`,
              isSignOut: true,
            },
          ])
      );
    }
  }, [companyData]);
  // #endregion

  return (
    <ThemeProvider theme={theme}>
      <CustomToolBar title={"客戶會員專區"} paths={paths} />
      <Box css={contentBox}>
        {/* Menu */}
        <Box
          sx={{ minWidth: "100px", maxWidth: "100%", justifySelf: "flex-end" }}
        >
          <MenuList sx={{ marginTop: {xs: 0, sm: "200px"} }}>
            {menuData.map((mData, index) => (
              <MenuItem
                key={index}
                css={menuValue === index ? menuCellSelected : menuCell}
                onClick={() => onClickMenu(index)}
              >
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  hidden={menuValue !== index}
                />
                {mData.buildingName}
              </MenuItem>
            ))}
          </MenuList>
        </Box>

        {/* Content */}
        <Stack
          spacing={2}
          sx={{ maxWidth: 884, alignSelf: "flex-start", justifySelf: "center" }}
        >
          <CompanyCard {...companyInfo} />
          <Tabs
            value={tabValue}
            onChange={onTabChange}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              "& .Mui-selected": {
                backgroundColor: "#009043",
                color: "#fff !important",
              },
            }}
            TabIndicatorProps={{ sx: { backgroundColor: "#fff0" } }}
          >
            {tabData.map((tabObj, index) => (
              <Tab key={index} label={tabObj} css={tabBtn} />
            ))}
            <Tab
              key={tabIndex.cre}
              label="線上建材設備選定系統"
              css={tabBtn}
              onClick={onClickCreTabBtn}
            />
            <Tab key={tabIndex.problem} label="常見問題" css={tabBtn} />
            <Tab key={tabIndex.changePd} label="修改密碼" css={tabBtn} />
          </Tabs>
          <Box css={tableHolder} hidden={tabValue !== 0}>
            <DataGrid
              pageSize={10}
              headerHeight={30}
              columns={columns}
              getRowId={(row) => row.projectProgressId}
              rows={progressRows}
              onCellClick={onClickCell}
              localeText={{
                noRowsLabel: "無工程進度資料",
                MuiTablePagination: {
                  labelDisplayedRows: ({ from, to, count }) =>
                    `第${from} - ${to}筆 , 共 ${count} 筆`,
                },
              }}
              disableColumnMenu
              disableColumnFilter
            />
          </Box>
          <Box css={tableHolder} hidden={tabValue !== 1}>
            <DataGrid
              pageSize={10}
              headerHeight={30}
              columns={payCols}
              rows={payRows}
              localeText={{
                noRowsLabel: "無繳款紀錄資料",
                MuiTablePagination: {
                  labelDisplayedRows: ({ from, to, count }) =>
                    `第${from} - ${to}筆 , 共 ${count} 筆`,
                },
              }}
              disableColumnMenu
              disableColumnFilter
            />
          </Box>
          <Box css={qaHolder} hidden={tabValue !== 3}>
            {qaRows.map(({ qus, ans }, index) => (
              <ProblemAccordion key={index} qus={qus} ans={ans} />
            ))}
          </Box>
          <Box sx={{ height: 400, width: "100%" }} hidden={tabValue !== 4}>
            <ChangePd submitEvent={onSendChangePd} />
          </Box>
        </Stack>
      </Box>
      <CustomFooter name={userName} isShowProfile={true} />
      {/* Dialog */}
      <Dialog
        onClose={onCloseDialog}
        open={isShow}
        maxWidth="md"
        fullWidth={true}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>
          檢視工程進度
        </DialogTitle>
        <DialogContent>
          <Stack sx={{ marginBottom: "15px" }}>
            <label>發佈日期 : {progressDetail?.publishDate}</label>
            <label>專案名稱 : {progressDetail?.name}</label>
            <label css={progressDesc}>
              本次進度描述 : {progressDetail?.description}
            </label>
          </Stack>
          {imageData.length > 0 && <Gallery imageData={imageData} />}
        </DialogContent>
      </Dialog>
      <Dialog onClose={onCloseMsgDialog} open={!!dilogMsg}>
        <DialogContent>{dilogMsg}</DialogContent>
        <Button
          sx={{ maxWidth: "200px", margin: "auto" }}
          onClick={onCloseMsgDialog}
        >
          確認
        </Button>
      </Dialog>
      <Backdrop open={isLoading || loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
  );
};

export default Home;
